import { convertCurrencyToNumber } from 'utils/currencyConverter';
import * as yup from 'yup';
import { getTotalDebt } from './refinancing.helpers';
import {
  FACILITY_FIELDS,
  FACILITY_WITH_DESCRIPTION,
  TConditionalFieldName,
  TFacilityType,
} from './refinancing.types';

// Facility types that require a given conditional field (i.e. it's listed in
// FACILITY_FIELDS and not marked optional). Derived from FACILITY_FIELDS so the
// validation stays in sync with the fields the form renders.
const facilityTypesRequiring = (fieldName: TConditionalFieldName) =>
  (Object.keys(FACILITY_FIELDS) as TFacilityType[]).filter((facilityType) =>
    FACILITY_FIELDS[facilityType].some(
      (field) => field.name === fieldName && !field.optional,
    ),
  );

const conditionalField = (fieldName: TConditionalFieldName, message: string) =>
  yup.string().when('facilityType', {
    is: (facilityType: TFacilityType) =>
      facilityTypesRequiring(fieldName).includes(facilityType),
    then: (fieldSchema) => fieldSchema.required(message),
  });

// Only applied when the card is shown (see `useShowRefinancing`); when hidden the whole `refinancing` branch is
// skipped so cached-but-hidden data never blocks submission.
export const refinancingSchema = yup
  .object({
    debts: yup
      .array()
      .of(
        yup.object({
          lenderName: yup.string().required('Lender name is required'),
          facilityType: yup.string().required('Type of facility is required'),
          outstandingBalance: yup
            .string()
            .required('Outstanding balance is required'),
          creditLimit: conditionalField(
            'creditLimit',
            'Credit limit is required',
          ),
          monthlyRepayment: conditionalField(
            'monthlyRepayment',
            'Monthly repayment is required',
          ),
          lastRepaymentDate: conditionalField(
            'lastRepaymentDate',
            'Last repayment date is required',
          ),
          // The description input only shows for the `other` facility, where it's
          // mandatory so we know what the debt actually is.
          otherDescription: yup.string().when('facilityType', {
            is: FACILITY_WITH_DESCRIPTION,
            then: (fieldSchema) =>
              fieldSchema.required('Description of facility is required'),
          }),
        }),
      )
      .min(1, 'Please add at least one debt'),
    usesLoanToClearDebts: yup
      .boolean()
      .oneOf([true], 'Please confirm how the loan will be used.'),
    hasEnteredAllDebts: yup
      .boolean()
      .oneOf([true], 'Please confirm all debts to be refinanced are entered.'),
  })
  // Block submission when the debts add up to more than the requested amount —
  // this mirrors the "over requested" error the debt summary already shows. The
  // requested `amount` lives on the parent form values, hence `this.parent`.
  .test(
    'debtsWithinRequestedAmount',
    'The requested amount should cover the total debt to refinance.',
    function (refinancing) {
      const requestedAmount = convertCurrencyToNumber(this.parent?.amount) || 0;

      // Skip until an amount is entered — the `amount` field raises its own
      // "required" error, and we don't want to stack a confusing over-requested
      // error on top of an empty amount.
      if (!requestedAmount) return true;

      const totalDebt = getTotalDebt(refinancing?.debts);

      return totalDebt <= requestedAmount;
    },
  );
